import React, { useState, useRef} from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";

const HustlersAvailable = () => {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();
    const userId = authState?.user?._id;

    const token = authState?.token;

    const [tags, setTags] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [message, setMessage] = useState("");
    const [statusMessage, setStatusMessage] = useState("");
    
    const [showHustler, setShowHustler] = useState(false);
    const [isAvailable, setIsAvailable] = useState(false);
    const [myTags, setMyTags] = useState("");
    const [newTagInput, setNewTagInput] = useState(""); // separate input state

    const hasFetched = useRef(false);

    const fetchMyHustlerStatus = async () => {
        if (hasFetched.current) return;
        try {
            setLoading(true);
            
            const res = await fetchData({
                url: "/pd/hustles/get-myhustle-status",
                method: "post",
                data: { userId: userId },
                headers: { Authorization: token },
            });

            if (res?.success && res.data) {                    
                setIsAvailable(res.data.isAvailable ?? false);
                setMyTags(Array.isArray(res.data.myTags) 
                    ? res.data.myTags.join(",") 
                    : (res.data.myTags || "")
                );
                hasFetched.current = true;
            } else {
                setIsAvailable(false);
                setMyTags("");
            }
        } catch (err) {
            console.error("fetchMyHustlerStatus error:", err);
            setIsAvailable(false);
            setMyTags("");
        } finally {
            setLoading(false);
        }
    };

    const updateMyHustleStatus = async () => {
        const config = {
            url: "/pd/hustles/update-myhustle-status",
            method: "post",
            data: {
                userId: userId,
                isAvailable: isAvailable,
                myTags: myTags
                    .split(",")
                    .map(t => t.trim())
                    .filter(Boolean),
            },
            headers: { Authorization: authState.token }
        };

        try {
            const response = await fetchData(config);
            const success = response?.success || response?.status;
            if (success) {
                hasFetched.current = false; 
            }
            setStatusMessage(success ? "✅ Updated Successfully" : "❌ Update Failed");
        } catch (err) {
            setStatusMessage("❌ Update Failed");
        }
    };

    const searchAvailable = async () => {
        if (!tags.trim()) {
            setMessage("⚠️ Enter at least one tag");
            return;
        }

        try {
            setLoading(true);

            const res = await fetchData({
                url: "/pd/hustles/iamavailable",
                method: "post",
                data: {
                    tags
                },
                headers: {
                    Authorization: token
                }
            });

            if (res?.status) {
                setResults(res.records || []);
                setMessage(`✅ Found ${res.count || 0} hustlers`);
            } else {
                setMessage("❌ No results found");
            }

        } catch (err) {
            console.error(err);
            setMessage("❌ Server error");
        } finally {
            setLoading(false);
        }
    };

    // Handle adding tags on Enter or Comma
    const handleTagInputKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const value = newTagInput.trim();
            if (value) {
                const currentTags = myTags ? myTags.split(",").map(t => t.trim()) : [];
                if (!currentTags.includes(value)) {
                    setMyTags(prev => prev ? `${prev},${value}` : value);
                }
                setNewTagInput("");
            }
        }
    };

    // Remove tag
    const removeTag = (index) => {
        const tagsArray = myTags.split(",").map(t => t.trim()).filter(Boolean);
        tagsArray.splice(index, 1);
        setMyTags(tagsArray.join(","));
    };

    const cleanNumber = (num) => {
        if (!num) return null;
        return String(num).replace(/[^\d]/g, "");
    };

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
    };

    const handleCall = (item) => {
        const number = cleanNumber(item.mobileNumber);

        if (!number) {
            alert("📵 No phone number available");
            return;
        }

        window.location.href = `tel:${number}`;
    };

    const handleWhatsApp = (item) => {
        const number = cleanNumber(item.whatsappNumber || item.mobileNumber);

        if (!number) {
            alert("💬 No WhatsApp number available");
            return;
        }

        const message = encodeURIComponent(
            `Hi ${item.userName}, I found your profile on Think5Whys Hustles. I am interested in connecting.`
        );

        window.open(`https://wa.me/${number}?text=${message}`, "_blank");
    };

    const handleEmail = (item) => {
        const email = item.emailId?.trim();

        if (!isValidEmail(email)) {
            alert("✉ No valid email available");
            return;
        }

        window.location.href = `mailto:${email}?subject=Hustle Inquiry from Think5Whys`;
    };

    return (
        <div className="space-y-3 pt-3">
            <div className="bg-[#111827] p-3 rounded-xl border border-gray-800">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-cyan-300 font-bold text-sm">
                        ⚡ Available Hustlers Network
                    </div>

                    <button
                        onClick={() => {
                            setShowHustler((prev) => {
                                const shouldShow = !prev;                                
                                if (shouldShow) {
                                    fetchMyHustlerStatus();
                                }
                                
                                return shouldShow;
                            });
                        }}
                        className="w-fit text-xs lg:text-sm text-gray-100 bg-blue-700 hover:bg-blue-800 px-2 py-1"
                    >
                        {showHustler ? "Hide Hustler Indication" : "Show Hustler Indication"}
                    </button>
                </div>

                <p className="text-xs text-gray-300 mt-1">
                    Find people based on skills, interests & availability
                </p>
                <input
                    className="w-full mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded"
                    placeholder="Enter tags (e.g. react, marketing, design)"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                />

                <button
                    onClick={searchAvailable}
                    disabled={loading}
                    className="w-fit px-2 py-1 mt-3 bg-cyan-500 text-black text-xs lg:text-sm font-bold rounded"
                >
                    {loading ? "Searching..." : "🔍 Search Hustlers"}
                </button>

                {message && (
                    <div className="text-xs text-yellow-300 mt-2">
                        {message}
                    </div>
                )}
            </div>

            {showHustler && (
                <div className="mt-2">
                    <div className="flex flex-col space-y-1">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs lg:text-sm text-gray-100">
                                Are You Available?
                            </label>
                            <select
                                className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                                value={isAvailable ? "true" : "false"}
                                onChange={(e) => setIsAvailable(e.target.value === "true")}   // ← Fixed
                            >
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="text-xs lg:text-sm text-gray-100">
                                I am Available Under Tags
                            </label>

                            <div className="flex flex-wrap gap-2 p-2 bg-gray-800 rounded-md min-h-[42px] items-center">
                                {myTags.split(",")
                                    .map(t => t.trim())
                                    .filter(Boolean)
                                    .map((tag, index) => (
                                        <span
                                            key={index}
                                            className="flex items-center gap-1 px-3 py-1 rounded-full bg-cyan-700 text-white text-xs lg:text-sm"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => removeTag(index)}
                                                className="text-white/70 hover:text-red-300 ml-1"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    ))}

                                <input
                                    type="text"
                                    value={newTagInput}                    // ← Use separate state
                                    onChange={(e) => setNewTagInput(e.target.value)}
                                    onKeyDown={handleTagInputKeyDown}      // ← Handle Enter / Comma
                                    placeholder="Add tags (comma or enter)..."
                                    className="flex-1 bg-transparent outline-none text-xs lg:text-sm text-white min-w-[120px]"
                                />
                            </div>

                            {statusMessage && (
                                <div className="text-xs text-center mb-2 text-yellow-300">
                                    {statusMessage}
                                </div>
                            )}

                            <button
                                onClick={updateMyHustleStatus}
                                className="w-fit px-2 py-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs"
                            >
                                Update My Status
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* RESULTS */}
            <div className="space-y-2">

                {results.map((item) => (
                    <div
                        key={item._id}
                        className="bg-[#0f172a] border border-gray-800 rounded-xl p-3"
                    >
                        {/* LIST VIEW */}
                        <div
                            className="flex justify-between cursor-pointer"
                            onClick={() =>
                                setSelected(selected?._id === item._id ? null : item)
                            }
                        >
                            <div>
                                <div className="text-sm font-semibold text-white">
                                    {item.userName}
                                </div>

                                <div className="text-[10px] text-gray-300">
                                    👤 {item.userCode}
                                </div>
                            </div>

                            <div className="text-[10px] text-green-400">
                                ACTIVE
                            </div>
                        </div>

                        {/* TAGS PREVIEW */}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {item.myTags?.slice(0, 5).map((t, i) => (
                                <span
                                    key={i}
                                    className="text-[10px] bg-gray-700 px-2 py-1 rounded"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>

                        {/* EXPANDED DETAILS */}
                        {selected?._id === item._id && (
                            <div className="mt-3 bg-[#111827] p-3 rounded-lg border border-cyan-500/20 space-y-3">

                                {/* TITLE */}
                                <div className="text-xs font-bold text-cyan-300">
                                    👤 Profile Details
                                </div>

                                <div className="text-sm font-semibold text-white">
                                    {item.userName}
                                </div>

                                <div className="text-xs text-gray-200">
                                    📍 {item.city || "Location not specified"}
                                </div>

                                {/* CONTACT INFO */}
                                <div className="text-xs text-gray-200 space-y-1">
                                    <div>📱 {item.mobileNumber || "No mobile"}</div>
                                    <div>📧 {item.emailId || "No email"}</div>
                                </div>

                                {/* TAGS */}
                                <div className="flex flex-wrap gap-2">
                                    {item.myTags?.map((t, i) => (
                                        <span key={i} className="text-[10px] bg-gray-700 px-2 py-1 rounded">
                                            {t}
                                        </span>
                                    ))}
                                </div>

                                {/* ACTION BUTTONS */}
                                <div className="grid grid-cols-3 gap-2 pt-2">

                                    <button
                                        onClick={() => handleCall(item)}
                                        className="text-[10px] w-fit px-2 py-1 rounded-md bg-green-500 text-black font-bold"
                                    >
                                        📞 Call
                                    </button>

                                    <button
                                        onClick={() => handleWhatsApp(item)}
                                        className="text-[10px] w-fit px-2 py-1 rounded-md bg-green-600 text-white font-bold"
                                    >
                                        💬 WhatsApp
                                    </button>

                                    <button
                                        onClick={() => handleEmail(item)}
                                        className="text-[10px] w-fit px-2 py-1 rounded-md bg-blue-600 text-white font-bold"
                                    >
                                        ✉ Email
                                    </button>
                                </div>

                                {/* ROLE */}
                                <div className="text-[10px] text-gray-300">
                                    🎯 Roles: {item.roles?.join(", ")}
                                </div>

                                {/* STATUS */}
                                <div className="text-[10px] text-cyan-400">
                                    ⚡ Availability: ACTIVE
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HustlersAvailable;